// src/email/api/hooks.js
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  emailApiClient,
  getStoredEmailToken,
  ensureEmailAuth,
  EMAIL_APIS_DISABLED,
} from './client';

export function useEmailAccounts() {
  return useQuery({
    queryKey: ['email-accounts'],
    queryFn: async () => {
      if (EMAIL_APIS_DISABLED) return [];
      await ensureEmailAuth();
      const res = await emailApiClient.get('/email/accounts');
      return res.data;
    },
    enabled: !EMAIL_APIS_DISABLED,
    refetchInterval: EMAIL_APIS_DISABLED ? false : 20000,
  });
}

export function useConnectGmail() {
  return useMutation({
    mutationFn: async () => {
      if (EMAIL_APIS_DISABLED) {
        console.warn('[Email Module] APIs are disabled.');
        return { url: null };
      }
      await ensureEmailAuth();
      const res = await emailApiClient.get('/email/accounts/oauth/connect-url');
      return res.data;
    },
    onSuccess: ({ url }) => {
      if (url) {
        window.location.href = url;
      }
    },
  });
}

export function useDisconnectAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId) => {
      if (EMAIL_APIS_DISABLED) return;
      return emailApiClient.delete(`/email/accounts/${accountId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
    },
  });
}


export function useSyncAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId) => {
      if (EMAIL_APIS_DISABLED) return {};
      const res = await emailApiClient.post(`/email/accounts/${accountId}/sync`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
    },
  });
}

export function useThreads(accountId, folder, cursor, limit = 50) {
  return useQuery({
    queryKey: ['threads', accountId, folder, cursor, limit],
    queryFn: async () => {
      if (EMAIL_APIS_DISABLED) return { items: [], nextCursor: null, hasMore: false };
      await ensureEmailAuth();
      const res = await emailApiClient.get(
        `/email/accounts/${accountId}/threads`,
        { params: { folder, ...(cursor ? { cursor } : {}), limit } }
      );
      return res.data;
    },
    enabled: !EMAIL_APIS_DISABLED && Boolean(accountId),
    refetchInterval: EMAIL_APIS_DISABLED ? false : 12000,
  });
}

// Key stays under ['threads', ...] so mutations and socket events refresh results too
export function useSearchThreads(accountId, query, limit = 50) {
  const q = (query || '').trim();
  return useInfiniteQuery({
    queryKey: ['threads', accountId, 'search', q, limit],
    queryFn: async ({ pageParam }) => {
      if (EMAIL_APIS_DISABLED) return { items: [], nextCursor: null };
      await ensureEmailAuth();
      const res = await emailApiClient.get(
        `/email/accounts/${accountId}/search`,
        { params: { q, ...(pageParam ? { cursor: pageParam } : {}), limit } }
      );
      return res.data;
    },
    initialPageParam: null,
    getNextPageParam: (last) => last?.nextCursor || undefined,
    enabled: !EMAIL_APIS_DISABLED && Boolean(accountId) && q.length > 0,
    placeholderData: keepPreviousData,
  });
}

// folder 'drafts' narrows the thread to its draft(s) and the mail chain they reply to
export function useThread(threadId, folder) {
  const draftsOnly = folder === 'drafts';
  return useQuery({
    queryKey: ['thread', threadId, draftsOnly ? 'drafts' : 'all'],
    queryFn: async () => {
      if (EMAIL_APIS_DISABLED) return null;
      await ensureEmailAuth();
      const res = await emailApiClient.get(`/email/threads/${threadId}`, {
        params: draftsOnly ? { folder: 'drafts' } : undefined,
      });
      return res.data;
    },
    enabled: !EMAIL_APIS_DISABLED && Boolean(threadId),
  });
}

// Drafts are addressed by their Gmail draftId; the draft's message id changes on every save
export async function fetchDraftId(accountId, messageId) {
  if (EMAIL_APIS_DISABLED) return null;
  await ensureEmailAuth();
  const res = await emailApiClient.get(
    `/email/accounts/${accountId}/drafts/by-message/${messageId}`
  );
  return res.data?.draftId;
}

function useDraftMutation(request) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars) => {
      if (EMAIL_APIS_DISABLED) return {};
      await ensureEmailAuth();
      const res = await request(vars);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['thread'] });
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
    },
  });
}

export const useCreateDraft = () =>
  useDraftMutation(({ accountId, payload }) =>
    emailApiClient.post(`/email/accounts/${accountId}/drafts`, payload)
  );
// Full replace: every field must be sent each time
export const useUpdateDraft = () =>
  useDraftMutation(({ accountId, draftId, payload }) =>
    emailApiClient.put(`/email/accounts/${accountId}/drafts/${draftId}`, payload)
  );
export const useSendDraft = () =>
  useDraftMutation(({ accountId, draftId, payload }) =>
    emailApiClient.post(`/email/accounts/${accountId}/drafts/${draftId}/send`, payload || {})
  );
export const useDiscardDraft = () =>
  useDraftMutation(({ accountId, draftId }) =>
    emailApiClient.delete(`/email/accounts/${accountId}/drafts/${draftId}`)
  );

export function useSendMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      if (EMAIL_APIS_DISABLED) return {};
      await ensureEmailAuth();
      const res = await emailApiClient.post('/email/send', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
    },
  });
}

function useMessageAction(pathFn, method = 'patch') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (messageId) => {
      if (EMAIL_APIS_DISABLED) return {};
      await ensureEmailAuth();
      return emailApiClient[method](pathFn(messageId));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['thread'] });
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
    },
  });
}

export const useMarkRead = () =>
  useMessageAction((id) => `/email/messages/${id}/read`);
export const useMarkUnread = () =>
  useMessageAction((id) => `/email/messages/${id}/unread`);
export const useStar = () =>
  useMessageAction((id) => `/email/messages/${id}/star`);
export const useUnstar = () =>
  useMessageAction((id) => `/email/messages/${id}/unstar`);
export const useTrash = () =>
  useMessageAction((id) => `/email/messages/${id}/trash`);
export const useUntrash = () =>
  useMessageAction((id) => `/email/messages/${id}/untrash`);
export const useMarkSpam = () =>
  useMessageAction((id) => `/email/messages/${id}/spam`);
export const useDeleteForever = () =>
  useMessageAction((id) => `/email/messages/${id}`, 'delete');

// Thread-level action hooks
export const useTrashThread = () =>
  useMessageAction((id) => `/email/threads/${id}/trash`);
export const useUntrashThread = () =>
  useMessageAction((id) => `/email/threads/${id}/untrash`);
export const useMarkThreadRead = () =>
  useMessageAction((id) => `/email/threads/${id}/read`);
export const useMarkThreadUnread = () =>
  useMessageAction((id) => `/email/threads/${id}/unread`);
export const useDeleteThreadForever = () =>
  useMessageAction((id) => `/email/threads/${id}`, 'delete');

export async function downloadOrViewAttachment(
  attachmentId,
  filename,
  forceDownload = false
) {
  if (EMAIL_APIS_DISABLED) {
    console.warn('[Email Module] Attachment download disabled (EMAIL_APIS_DISABLED = true)');
    return;
  }
  const token = getStoredEmailToken();
  const base = emailApiClient.defaults.baseURL || '';
  const url = `${base}/email/attachments/${attachmentId}/download${
    token ? `?token=${encodeURIComponent(token)}` : ''
  }${forceDownload ? (token ? '&download=true' : '?download=true') : ''}`;

  if (!forceDownload) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  try {
    const res = await emailApiClient.get(
      `/email/attachments/${attachmentId}/download?download=true`,
      { responseType: 'blob' }
    );
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
  } catch (err) {
    console.error('Failed to download attachment', err);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
